import ContractAttachment from '@/components/ContractAttachment'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { Badge } from '@/components/ui/badge'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useTabs from '@/hooks/useTabs'
import ServiceActiveIcons from '@/icons/ServiceActiveIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import { formatCurrency, formatDate, uniqBy } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { AlertCircle, FileText, MessageSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { Tabs, Typogaphy } from 'vnetwork-ui'
import { ProcessListContent } from '../../modules/Process'
import { getContractProcesses } from '../../modules/Process/services'
import SubcontractModules from '../../modules/Subcontract'
import { Subcontract } from '../../modules/Subcontract/types'
import ContractUpdateModules from '../../modules/Updates'
import { getContractCustomer } from '../../services/customer'
import { ContractService, ContractStatus, CustomerContract } from '../../types'
import Suggestions from './Suggestions'
import ContractInfoCard from './contract-info-card'

export const renderServicesDisplay = (
    contract: CustomerContract | Subcontract,
    t: any
) => {
    const services = contract?.services || []
    const service = services?.[0] || ({} as ContractService)

    if (services?.length === 0)
        return {
            label: t('contract_service'),
            value: contract?.service_id + ' - ' + (service?.service_name || ''),
            hidden: true,
        }
    if (services.length <= 1) {
        const service = services?.[0] || ({} as ContractService)
        return {
            label: t('contract_service'),
            value: service?.service_id + ' - ' + (service?.service_name || ''),
            component: 'Link',
            componentProps: {
                to: generatePathDatacentral(paths.serviceDetail, {
                    id: service?.service_id,
                }),
            },
        }
    }

    return {
        label: t('contract_services'),
        value: `${services.length} ${t('services_count')}`,
        component: 'Category' as const,
        className: 'md:col-span-2 xs:col-span-1',
    }
}

export const ServicesCardsDisplay = ({
    services,
}: {
    services: ContractService[]
}) => {
    const { t } = useTranslation('contract')
    const allServices = [...services]

    if (allServices.length <= 1) return null

    return (
        <Paper className="mt-2">
            <div className="flex items-center gap-2 mb-3">
                <ServiceIcons className="w-5 h-5 text-primary" />
                <Typogaphy className="font-semibold text-primary">
                    {t('contract_services')} ({allServices.length})
                </Typogaphy>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allServices.map((service, index) => (
                    <div
                        key={`${service.service_id}-${index}`}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 hover:border-primary/20"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10">
                                    <ServiceActiveIcons className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex flex-col">
                                    <Typogaphy className="font-medium text-sm text-gray-900">
                                        {service.service_name}
                                    </Typogaphy>
                                    <Typogaphy className="text-xs text-gray-500">
                                        ID: {service.service_id}
                                    </Typogaphy>
                                </div>
                            </div>
                            <Link
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    { id: service.service_id }
                                )}
                                className="text-primary hover:text-primary/80 text-xs font-medium hover:underline"
                            >
                                {t('view_detail')}
                            </Link>
                        </div>
                        <div className="space-y-2">
                            {service.product_group_name && (
                                <div className="flex justify-between items-center">
                                    <Typogaphy className="text-xs text-gray-500">
                                        {t('product_group')}:
                                    </Typogaphy>
                                    <Typogaphy className="text-xs font-medium text-gray-700">
                                        {service.product_group_name}
                                    </Typogaphy>
                                </div>
                            )}
                            {service.service_status && (
                                <div className="flex justify-between items-center">
                                    <Typogaphy className="text-xs text-gray-500">
                                        {t('status')}:
                                    </Typogaphy>
                                    <div
                                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            service.service_status === 'active'
                                                ? 'bg-green-100 text-green-700'
                                                : service.service_status ===
                                                    'pending'
                                                  ? 'bg-yellow-100 text-yellow-700'
                                                  : 'bg-gray-100 text-gray-700'
                                        }`}
                                    >
                                        {t(service.service_status)}
                                    </div>
                                </div>
                            )}
                            {service.contract_value && (
                                <div className="flex justify-between items-center">
                                    <Typogaphy className="text-xs text-gray-500">
                                        {t('contract_value')}:
                                    </Typogaphy>
                                    <Typogaphy className="text-xs font-medium text-gray-700">
                                        {formatCurrency(service.contract_value)}
                                    </Typogaphy>
                                </div>
                            )}
                            {service?.start_date && service?.end_date && (
                                <div className="flex justify-between items-center">
                                    <Typogaphy className="text-xs text-gray-500">
                                        {t('duration')}:
                                    </Typogaphy>
                                    <Typogaphy className="text-xs font-medium text-gray-700">
                                        {formatDate(service.start_date)} -{' '}
                                        {formatDate(service.end_date)}
                                    </Typogaphy>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </Paper>
    )
}

const Detail = () => {
    const { t } = useTranslation('contract')
    const { contractID } = useParams<{ contractID: string }>()

    const { data: contract, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS, Number(contractID)],
        queryFn: async () => {
            const result = await getContractCustomer({
                contractID: Number(contractID) || 0,
            })
            return result as { result: CustomerContract }
        },
    })

    const { data: processes } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTRACTS_PROCESSES, Number(contractID)],
            queryFn: () =>
                getContractProcesses({ contractID: Number(contractID) }),
        },
        { showLoading: false, showError: false }
    )

    const processLabel = processes?.length
        ? `${t('contract-process-list')} (${processes.length})`
        : t('contract-process-list')

    const { TabProps, TabComponent } = useTabs({
        tabs: [
            {
                label: t('subcontract-list'),
                value: 'subcontracts',
                startIcon: <FileText className="w-4 h-4" />,
                component: (
                    <SubcontractModules.List
                        contractID={Number(contractID)}
                        contract={contract?.result}
                    />
                ),
            },
            {
                label: processLabel,
                value: 'processes',
                startIcon: <MessageSquare className="w-4 h-4" />,
                component: (
                    <ProcessListContent contractID={Number(contractID)} />
                ),
            },
        ],
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const data = contract?.result
    return (
        <div className="mb-2">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.customerContractList,
                        title: t('contract-list'),
                    },
                    {
                        path: '',
                        title: `${contract?.result?.contract_number_id}`,
                    },
                ]}
            />

            {/* Language warning */}
            {data?.customer_language && data.customer_language !== 'vi' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-2 flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <div className="flex items-center gap-2 flex-wrap">
                        <Typogaphy className="text-sm text-orange-800 font-medium">
                            {t('non-vietnamese-language')}:
                        </Typogaphy>
                        <Badge
                            variant="default"
                            className="uppercase bg-orange-500 hover:bg-orange-600"
                        >
                            {data.customer_language}
                        </Badge>
                    </div>
                </div>
            )}

            {/* Contract information card */}
            {data && (
                <Paper className="p-4 mt-2">
                    <ContractInfoCard contract={data} contractID={contractID} />
                </Paper>
            )}

            {/* Suggestions (pending contracts only) */}
            {data?.contract_status === ContractStatus.Pending && (
                <Suggestions contractID={Number(contractID)} />
            )}

            {/* Multiple services cards */}
            {(data?.services?.length || 0) > 1 && (
                <ServicesCardsDisplay services={data?.services!} />
            )}

            {/* Attachments */}
            {data && data.attachments?.length > 0 && (
                <Paper className="mt-2">
                    <ContractAttachment
                        attachments={uniqBy(data.attachments, 'attachment_id')}
                    />
                </Paper>
            )}

            {/* Tabs: subcontracts + processes */}
            <Tabs {...TabProps} className="mt-2" />
            {TabComponent}

            {/* Activity log */}
            <ContractUpdateModules.List contractID={Number(contractID)} />
        </div>
    )
}

export const DetailBusiness = () => <Detail />
